import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MapPin, Phone, Mail, Globe, Clock, FileText, Filter } from 'lucide-react';
import { recommendations as recommendationsApi } from '../utils/api';

const Recommendations = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recsRes, catsRes] = await Promise.all([
        recommendationsApi.getAll(),
        recommendationsApi.getCategories(),
      ]);
      setRecommendations(recsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Load recommendations error:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const filteredRecommendations = selectedCategory === 'all'
    ? recommendations
    : recommendations.filter(rec => rec.category === selectedCategory);

  const getCategoryLabel = (category) => {
    return categories.find(c => c.value === category)?.label || category;
  };

  const renderRecommendationCard = (rec) => (
    <div key={rec.id} className="card hover:shadow-xl transition-all duration-300">
      <div className="flex items-start space-x-3 mb-3">
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-gray-900 mb-1">{rec.business_name || rec.name}</h3>
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">
            {getCategoryLabel(rec.category)}
          </span>
        </div>
      </div>

      {rec.description && (
        <p className="text-gray-700 mb-4 leading-relaxed">{rec.description}</p>
      )}

      <div className="space-y-3">
        {rec.address && (
          <div className="flex items-start space-x-3 text-gray-600">
            <MapPin size={20} className="mt-0.5 flex-shrink-0 text-primary-600" />
            <div>
              <p className="font-medium text-gray-900">Address</p>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(rec.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-600 transition-colors"
              >
                {rec.address}
              </a>
            </div>
          </div>
        )}

        {rec.phone && (
          <div className="flex items-start space-x-3 text-gray-600">
            <Phone size={20} className="flex-shrink-0 text-primary-600" />
            <div>
              <p className="font-medium text-gray-900">Phone</p>
              <a href={`tel:${rec.phone}`} className="hover:text-primary-600 transition-colors">
                {rec.phone}
              </a>
            </div>
          </div>
        )}

        {rec.email && (
          <div className="flex items-start space-x-3 text-gray-600">
            <Mail size={20} className="flex-shrink-0 text-primary-600" />
            <div>
              <p className="font-medium text-gray-900">Email</p>
              <a href={`mailto:${rec.email}`} className="hover:text-primary-600 transition-colors">
                {rec.email}
              </a>
            </div>
          </div>
        )}

        {rec.website && (
          <div className="flex items-start space-x-3 text-gray-600">
            <Globe size={20} className="flex-shrink-0 text-primary-600" />
            <div>
              <p className="font-medium text-gray-900">Website</p>
              <a
                href={rec.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-600 transition-colors break-all"
              >
                {rec.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          </div>
        )}

        {rec.hours && (
          <div className="flex items-start space-x-3 text-gray-600">
            <Clock size={20} className="flex-shrink-0 text-primary-600" />
            <div>
              <p className="font-medium text-gray-900">Hours</p>
              <p>{rec.hours}</p>
            </div>
          </div>
        )}

        {rec.notes && (
          <div className="flex items-start space-x-3 text-gray-600 mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <FileText size={20} className="mt-0.5 flex-shrink-0 text-blue-600" />
            <div>
              <p className="font-medium text-gray-900 mb-1">Additional Information</p>
              <p className="italic text-gray-700">{rec.notes}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Recommended Services</h1>
          <p className="text-gray-600 text-lg">
            Trusted businesses and services we recommend
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Filter size={20} className="text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filter by Category</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                selectedCategory === 'all'
                  ? 'bg-primary-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              All Categories
            </button>
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  selectedCategory === cat.value
                    ? 'bg-primary-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Recommendations Grid */}
        {filteredRecommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRecommendations.map(rec => renderRecommendationCard(rec))}
          </div>
        ) : (
          <div className="card text-center py-16">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No recommendations found
            </h2>
            <p className="text-gray-600">
              {selectedCategory === 'all'
                ? "We haven't added any recommendations yet. Check back soon!"
                : `No recommendations in the ${getCategoryLabel(selectedCategory)} category yet.`}
            </p>
          </div>
        )}

        {/* Info Box */}
        {recommendations.length > 0 && (
          <div className="mt-12 card bg-blue-50 border-2 border-blue-200">
            <h3 className="text-xl font-bold text-gray-900 mb-3">About These Recommendations</h3>
            <p className="text-gray-700 leading-relaxed">
              These are businesses and services we personally recommend to our clients. We've worked with or 
              researched each of these providers and believe they offer quality service. However, please do 
              your own research and make decisions that are best for you.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recommendations;
